import {
    formatCalendarCellDate,
    formatDateString,
    getLocalDateString,
    getMondayForDate,
    getWeekKeyFromDate,
} from "./calendarMonthUtils";

export function getBookingForDate(bookingDateStr, weekData = {}, bookingSpotData = []) {
    const weekMatch = Object.values(weekData || {})
        .flat()
        .find((booking) => booking.BookingDate === bookingDateStr);

    return weekMatch || (bookingSpotData || []).find((booking) => booking.BookingDate === bookingDateStr) || null;
}

/**
 * Is this cell past the account's last bookable day?
 *
 * `LastBookingDate` (Calendar_api `Data.LastBookingDate`) is the LAST day that can
 * still be booked, so the comparison is strictly greater - the boundary day itself
 * stays bookable. Both sides are flattened to midnight so a timestamp on either
 * cannot tip the result.
 *
 * @param {Date} cellDate the day cell being rendered
 * @param {Date|null} lastBookingDate parsed LastBookingDate, or null when unknown
 * @returns {boolean} false whenever lastBookingDate is missing or unparseable, so
 *          an absent field never greys the calendar out
 */
export function isAfterLastBookingDate(cellDate, lastBookingDate) {
    if (!(cellDate instanceof Date) || Number.isNaN(cellDate.getTime())) return false;
    if (!(lastBookingDate instanceof Date) || Number.isNaN(lastBookingDate.getTime())) return false;

    const cellMidnight = new Date(
        cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()
    ).getTime();
    const lastMidnight = new Date(
        lastBookingDate.getFullYear(), lastBookingDate.getMonth(), lastBookingDate.getDate()
    ).getTime();

    return cellMidnight > lastMidnight;
}

/**
 * Exclusive end of the navigable range: one year from the zone's current date,
 * rounded up to the end of that month (RONLARAVEL-78).
 *
 * Rounding up matters. FullCalendar's `validRange.end` is EXCLUSIVE and it clips
 * the rendered grid, so passing the bare anniversary (7 Sep 2027) would render a
 * truncated September with its later days blank. Returning 1 Oct 2027 instead lets
 * September 2027 draw as a full month and disables `next` once past it.
 *
 * @param {string|Date} zoneCurrentDateValue Records.ZoneCurrentDate, e.g. "2026-09-07"
 * @returns {Date|null} exclusive upper bound, or null when the input is unusable -
 *          callers must then leave the range open rather than guess a cap
 */
export function getCalendarNavigationEndDate(zoneCurrentDateValue) {
    const zoneCurrentDate = zoneCurrentDateValue instanceof Date
        ? zoneCurrentDateValue
        : formatDateString(zoneCurrentDateValue);

    if (!zoneCurrentDate || Number.isNaN(zoneCurrentDate.getTime())) {
        return null;
    }

    // Day 1 of the month AFTER the anniversary month. Month index +13 rolls the
    // year over on its own, and day 1 sidesteps short-month clamping entirely.
    const navigationEndDate = new Date(
        zoneCurrentDate.getFullYear(),
        zoneCurrentDate.getMonth() + 13,
        1
    );
    navigationEndDate.setHours(0, 0, 0, 0);

    return navigationEndDate;
}

/**
 * Is this month at or past the navigable end bound?
 *
 * @param {Date} monthDateValue first day of the month being navigated to
 * @param {Date|null} navigationEndDate from getCalendarNavigationEndDate
 * @returns {boolean} false when the bound is unknown, so navigation stays open
 */
export function isBeyondNavigationEnd(monthDateValue, navigationEndDate) {
    if (!(monthDateValue instanceof Date) || Number.isNaN(monthDateValue.getTime())) return false;
    if (!(navigationEndDate instanceof Date) || Number.isNaN(navigationEndDate.getTime())) return false;

    const monthStart = new Date(
        monthDateValue.getFullYear(), monthDateValue.getMonth(), 1
    ).getTime();

    // end is exclusive: a month starting exactly on it is already out of range
    return monthStart >= navigationEndDate.getTime();
}

/**
 * Whether a Calendar_api response is for the week containing the zone's current day.
 *
 * CurrentDayBookingRecords (today's bookings, which drive the Check in / Check out toolbar
 * button) is only filled in the response for that week; every other week returns it empty,
 * so only this week's response may replace it.
 *
 * @param {string|Date} zoneCurrentDate Records.ZoneCurrentDate from the response
 * @param {string|Date} requestedWeekStart any day of the week that was requested
 * @returns {boolean} true when either value is unknown, which keeps the old
 *   overwrite behaviour for callers that don't say which week they requested
 */
export function isCurrentDayWeekResponse(zoneCurrentDate, requestedWeekStart) {
    if (!requestedWeekStart) return true;

    const currentDayMonday = getLocalDateString(getMondayForDate(zoneCurrentDate));
    if (!currentDayMonday) return true;

    const requestedMonday = getLocalDateString(getMondayForDate(requestedWeekStart));
    if (!requestedMonday) return true;

    return requestedMonday === currentDayMonday;
}

/**
 * The waitlist (Join Queue) flag for the member the calendar is showing.
 *
 * Records.QueueUser is the logged-in user's own flag (for a team leader, the leader's).
 * Each TeamMemberList entry carries that member's flag, and it can differ - e.g. the
 * waitlist is off for the leader but on for a team member. So:
 * - TeamMemberList empty → Records.QueueUser
 * - otherwise → the selected member's QueueUser, falling back to Records.QueueUser when
 *   that member is not in the list
 *
 * @param {object} records Calendar_api Data.Records
 * @param {string} memberGuid GuId of the member the request was made for
 * @returns {number} the QueueUser flag (0 when unknown)
 */
export function resolveSelectedQueueUser(records, memberGuid) {
    const members = Array.isArray(records?.TeamMemberList) ? records.TeamMemberList : [];

    if (members.length === 0) return records?.QueueUser ?? 0;

    const member = members.find((teamMember) => teamMember.GuId === memberGuid);
    return member?.QueueUser ?? records?.QueueUser ?? 0;
}

export function resolveDayCellState({
    date,
    selectedDate,
    weekData = {},
    bookingSpotData = [],
    lastBookingDate = null,
    today = new Date(),
}) {
    const currentDate = date;
    const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    const currentDateString = formatCalendarCellDate(currentDate);
    const isSelected = currentDateString === selectedDate;

    const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const cellDateMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
    const isPastDate = cellDateMidnight < todayMidnight;

    const weekKey = getWeekKeyFromDate(currentDate);
    const currentWeekMonday = new Date(todayMidnight);
    const currentWeekDay = currentWeekMonday.getDay();
    const currentWeekDiff = currentWeekMonday.getDate() - currentWeekDay + (currentWeekDay === 0 ? -6 : 1);
    currentWeekMonday.setDate(currentWeekDiff);
    currentWeekMonday.setHours(0, 0, 0, 0);

    const mondayOfWeek = new Date(cellDate);
    const dayOfWeek = mondayOfWeek.getDay();
    const diff = mondayOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    mondayOfWeek.setDate(diff);
    mondayOfWeek.setHours(0, 0, 0, 0);

    const isCurrentWeek = currentWeekMonday.getTime() === mondayOfWeek.getTime();
    const hasFetchedData = Object.prototype.hasOwnProperty.call(weekData, weekKey);

    const monthWeekBookings = (bookingSpotData || []).filter(
        (booking) => getWeekKeyFromDate(booking?.BookingDate) === weekKey
    );

    const weekBookings = (hasFetchedData || isCurrentWeek)
        ? (weekData[weekKey] || monthWeekBookings)
        : monthWeekBookings;

    const boundaryBookingOnDate = monthWeekBookings.find((booking) => {
        const bookingDate = formatDateString(booking.BookingDate);
        return (
            bookingDate &&
            bookingDate.getFullYear() === cellDate.getFullYear() &&
            bookingDate.getMonth() === cellDate.getMonth() &&
            bookingDate.getDate() === cellDate.getDate()
        );
    });

    // Two independent reasons a cell is force-disabled:
    //  - it is a neighbouring-month boundary cell (the original rule), or
    //  - it falls past LastBookingDate, so it can never be booked. That arm needs
    //    no schedule data, which is what lets the month past the boundary render
    //    correctly with its API call skipped entirely.
    const isBoundaryDisabledDate =
        (Number(boundaryBookingOnDate?.CurrentMonth) === 0 &&
            Number(boundaryBookingOnDate?.ActiveMonth) === 0) ||
        isAfterLastBookingDate(cellDate, lastBookingDate);

    const bookingOnDate = (hasFetchedData || isCurrentWeek)
        ? weekBookings.find((booking) => {
            const bookingDate = formatDateString(booking.BookingDate);
            return (
                bookingDate &&
                bookingDate.getFullYear() === cellDate.getFullYear() &&
                bookingDate.getMonth() === cellDate.getMonth() &&
                bookingDate.getDate() === cellDate.getDate()
            );
        })
        : isBoundaryDisabledDate
            ? boundaryBookingOnDate
            : undefined;

    return {
        bookingOnDate,
        cellDate,
        currentDateString,
        dateNumber: currentDate.getDate(),
        hasFetchedData,
        isBoundaryDisabledDate,
        isCurrentWeek,
        isPastDate,
        isSelected,
        monthWeekBookings,
        weekBookings,
        weekKey,
    };
}
